import React, { useState, useEffect } from 'react';
import { 
  useB2BFormValidation, 
  ValidationRules,
  B2B_POSITION_OPTIONS, 
  B2B_INTERESTED_IN_OPTIONS 
} from '../B2BShared';
import { useB2BTranslation } from '../B2BShared/hooks/useB2BTranslation';
import B2BParticipantHeader from './components/B2BParticipantHeader';
import B2BParticipantAvatarSection from './components/B2BParticipantAvatarSection';
import { B2BFormField, useB2BFormField } from '../B2BShared/components/B2BFormField';
import B2BParticipantDisplay from './components/B2BParticipantDisplay';
import B2BParticipantActions from './components/B2BParticipantActions';
import { translatePosition } from '../B2BShared/utils/translatePosition';
import './B2BParticipantsCard.css';

interface Business {
  businessName: string;
  businessWebsite: string;
  businessCategory: string | string[];
  businessDescription: string;
  productsServices: string;
  socialMedia: string;
  countriesOfOperation: string[];
  logo?: File | null;
}

interface Participant {
  id?: string;
  name: string;
  surname: string;
  email: string;
  phoneNumber?: string;
  position: string | string[];
  customPosition?: string;
  linkedin?: string;
  profilePhoto?: string | null;
  birthday?: string | Date | { $date: { $numberLong: string } };
  cityOfBirth?: string;
  businesses?: Business[];
  interestedIn?: string[];
}

interface B2BParticipantsCardProps {
  participant: Participant;
  isEditing?: boolean;
  onSave?: (data: Participant) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  isOwnProfile?: boolean;
  currentUser?: any;
}

const B2BParticipantsCard: React.FC<B2BParticipantsCardProps> = ({
  participant,
  isEditing = false,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  isOwnProfile = false,
  currentUser = null,
}) => {
  const { t, tDashboard, tAuth, tDashboardExists } = useB2BTranslation();
  const { validateForm, clearErrors } = useB2BFormValidation();
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Initialize B2BFormField hook
  const { getFieldProps } = useB2BFormField();


  // Helper function to format birthday for display
  const formatBirthday = (birthday: string | Date | null): string => {
    console.log('formatBirthday called with:', birthday);
    if (!birthday) return '';
    
    try {
      // Handle ISO string format like "1983-09-06T00:00:00.000Z"
      let date: Date;
      if (typeof birthday === 'string' && birthday.includes('T')) {
        // Extract just the date part from ISO string
        const datePart = birthday.split('T')[0];
        console.log('Extracted date part:', datePart);
        date = new Date(datePart);
      } else {
        date = new Date(birthday);
      }
      
      console.log('Created date object:', date);
      if (isNaN(date.getTime())) return '';
      
      // Format as DD/MM/YYYY
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      const result = `${day}/${month}/${year}`;
      console.log('Formatted result:', result);
      return result;
    } catch (error) {
      console.error('Error formatting birthday:', error);
      return '';
    }
  };

  const [formData, setFormData] = useState<Participant & { birthday: string }>({
    name: participant?.name || '',
    surname: participant?.surname || '',
    email: participant?.email || '',
    phoneNumber: participant?.phoneNumber || '',
    position: Array.isArray(participant?.position) ? participant.position : (participant?.position ? [participant.position] : ['owner']),
    customPosition: participant?.customPosition || '',
    linkedin: participant?.linkedin || '',
    profilePhoto: participant?.profilePhoto || null,
    birthday: '',
    cityOfBirth: participant?.cityOfBirth || '',
    businesses: participant?.businesses?.length > 0 
      ? participant.businesses 
      : [{
          businessName: '',
          businessWebsite: '',
          businessCategory: [],
          businessDescription: '',
          productsServices: '',
          socialMedia: '',
          countriesOfOperation: [],
          logo: null,
        }],
    interestedIn: Array.isArray(participant?.interestedIn) 
      ? participant.interestedIn 
      : [],
  });

  const validationRules: ValidationRules = {
    name: ['required', 'name'],
    surname: ['required', 'name'],
    email: ['required', 'email'],
    phoneNumber: ['phone'],
    position: ['required'],
    linkedin: ['url'],
  };

  // Update formData when participant prop changes
  useEffect(() => {
    if (participant) {
      console.log('Loading participant data:', participant);
      
      // Format birthday if it exists
      let formattedBirthday = '';
      if (participant.birthday) {
        if (typeof participant.birthday === 'string') {
          formattedBirthday = participant.birthday;
        } else if (participant.birthday instanceof Date) {
          formattedBirthday = formatBirthday(participant.birthday.toISOString());
        } else if (participant.birthday.$date) {
          // Handle MongoDB date format
          const date = new Date(participant.birthday.$date.$numberLong);
          formattedBirthday = formatBirthday(date.toISOString());
        }
      }
      
      // Normalize position to array format
      const positionValue = Array.isArray(participant.position) 
        ? participant.position 
        : (participant.position ? [participant.position] : ['owner']);
      
      setFormData({
        name: participant.name || '',
        surname: participant.surname || '',
        email: participant.email || '',
        phoneNumber: participant.phoneNumber || '',
        position: positionValue,
        customPosition: participant.customPosition || '',
        linkedin: participant.linkedin || '',
        profilePhoto: participant.profilePhoto || null,
        birthday: formattedBirthday,
        cityOfBirth: participant.cityOfBirth || '',
        businesses: participant.businesses?.length > 0 
          ? participant.businesses 
          : [{
              businessName: '',
              businessWebsite: '',
              businessCategory: [],
              businessDescription: '',
              productsServices: '',
              socialMedia: '',
              countriesOfOperation: [],
              logo: null,
            }],
        interestedIn: Array.isArray(participant.interestedIn) 
          ? participant.interestedIn 
          : [],
      });
      
      console.log('Form data set to:', {
        originalPosition: participant.position,
        processedPosition: positionValue,
        linkedin: participant.linkedin,
        birthday: formattedBirthday,
        interestedIn: participant.interestedIn
      });
    }
  }, [participant]);

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          profilePhoto: e.target?.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    clearErrors();
    
    if (validateForm(formData, validationRules)) {
      // Process the form data to match backend expectations
      const processedData = {
        ...formData,
        // Position is now an array — pass through directly
        position: Array.isArray(formData.position) ? formData.position : (formData.position ? [formData.position] : []),
      };
      
      console.log('Saving participant data:', processedData);
      onSave?.(processedData);
    }
  };

  const handleCancel = () => {
    clearErrors();
    onCancel?.();
  };

  // Position options for dropdown
  const positionOptions = B2B_POSITION_OPTIONS.map(pos => ({
    value: pos,
    label: tDashboard(`profile.positionOptions.${pos}`)
  }));

  // Interested in options for checkboxes
  const interestedInOptions = B2B_INTERESTED_IN_OPTIONS.map(interest => ({
    value: interest,
    label: tAuth(`interestedInOptions.${interest}`)
  }));

  if (isEditing) {
    return (
      <div className="b2b-card-wrapper editing">
        <div className="b2b-card editing">
          <B2BParticipantHeader
            name={formData.name}
            surname={formData.surname}
            position={formData.position}
            profilePhoto={formData.profilePhoto}
            isEditing={true}
          />

          <B2BParticipantAvatarSection
            profilePhoto={formData.profilePhoto}
            onAvatarChange={handleAvatarChange}
          />

          <div className="b2b-profile-form-section">
            <h4>{t('auth.requiredInformation')}</h4>

            <div className="form-row">
              <B2BFormField
                {...getFieldProps("name", "text")}
                label={tAuth("name")}
                placeholder={tDashboard("profile.placeholders.name")}
                required
              />
              
              <B2BFormField
                {...getFieldProps("surname", "text")}
                label={tAuth("surname")}
                placeholder={tDashboard("profile.placeholders.surname")}
                required
              />
            </div>

            <div className="form-row">
              <B2BFormField
                {...getFieldProps("email", "email")}
                label={tAuth("email")}
                placeholder={tDashboard("profile.placeholders.email")}
                required
              />
              
              <B2BFormField
                {...getFieldProps("phoneNumber", "tel")}
                label={tAuth("phoneNumber")}
                placeholder={tDashboard("profile.placeholders.phonePrivate")}
              />
            </div>

            <div className="form-row">
              <B2BFormField
                {...getFieldProps("position", "select")}
                label={tAuth("position")}
                options={positionOptions}
                required
              />
              
              {/* Custom position field — only shown if position array contains 'other' */}
              {(Array.isArray(formData.position) ? formData.position.includes('other') : formData.position === 'other') && (
                <B2BFormField
                  {...getFieldProps("customPosition", "text")}
                  label={tAuth("customPosition")}
                  placeholder={tDashboard("profile.placeholders.customPosition")}
                  required
                />
              )}
              
              <B2BFormField
                {...getFieldProps("linkedin", "url")}
                label={tAuth("linkedin")}
                placeholder={tDashboard("profile.placeholders.linkedin")}
              />
            </div>

            <h4>{t('auth.optionalInformation')}</h4>

            <B2BFormField
              {...getFieldProps("interestedIn", "checkbox-group")}
              label={tAuth("interestedIn")}
              options={interestedInOptions}
            />

          </div>

          <B2BParticipantActions
            isOwnProfile={isOwnProfile}
            currentUser={currentUser}
            participant={participant}
            onEdit={onEdit!}
            onDelete={onDelete!}
            isEditing={true}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </div>
      </div>
    );
  }

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Get countries for display
  const countries = participant.businesses?.flatMap(business => business.countriesOfOperation || []).filter(Boolean) || [];
  const uniqueCountries = Array.from(new Set(countries));
  

  return (
    <div className="b2b-card-wrapper">
      <div className="b2b-card">
        {/* Simplified Header */}
        <div className="card-header-simplified">
          <div className="participant-info">
            <h3 className="participant-name">
              {participant.name} {participant.surname}
            </h3>

            {/* Email — right under the name */}
            {participant.email && (
              <div className="participant-email">
                <a href={`mailto:${participant.email}`}>{participant.email}</a>
              </div>
            )}

            <p className="participant-position">
              {translatePosition(participant.position, tDashboard, "profile.positionOptions", tDashboardExists)}
            </p>
            
            {/* Business Name */}
            {participant.businesses && participant.businesses.length > 0 && participant.businesses[0].businessName && (
              <div className="business-name">
                <span className="business-name-tag">
                  {participant.businesses[0].businessName}
                </span>
              </div>
            )}

            {/* Countries */}
            {uniqueCountries.length > 0 && (
              <div className="countries">
                {uniqueCountries.map((country, index) => {
                  const key = `profile.countries.${country}`;
                  let translated = tDashboard(key);
                  
                  const isMissing = (val: string, k: string) =>
                    val === k || val === `dashboard.${k}`;
                  
                  if (isMissing(translated, key)) {
                    const lowerKey = `profile.countries.${country.toLowerCase()}`;
                    const lowerTranslated = tDashboard(lowerKey);
                    if (!isMissing(lowerTranslated, lowerKey)) {
                      translated = lowerTranslated;
                    }
                  }
                  
                  if (isMissing(translated, key) || isMissing(translated, `profile.countries.${country.toLowerCase()}`)) {
                    translated = country
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())
                      .trim();
                  }
                  
                  return (
                    <span key={index} className="country-tag">
                      {translated}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Expandable Content */}
        {isExpanded && (
          <div className="card-content-expanded">
            <B2BParticipantDisplay participant={{
              ...participant,
              birthday: typeof participant.birthday === 'string' 
                ? participant.birthday 
                : participant.birthday instanceof Date 
                  ? participant.birthday.toISOString()
                  : participant.birthday && typeof participant.birthday === 'object' && '$date' in participant.birthday
                    ? new Date(participant.birthday.$date.$numberLong).toISOString()
                    : ''
            }} />
          </div>
        )}

        {/* Action Buttons */}
        <div className="card-actions-simplified">
          <button 
            className="expand-btn"
            onClick={handleToggleExpanded}
          >
            {isExpanded ? t('showLess') : t('showMore')}
          </button>
          
          {isOwnProfile && (
            <B2BParticipantActions
              isOwnProfile={isOwnProfile}
              currentUser={currentUser}
              participant={participant}
              onEdit={onEdit!}
              onDelete={onDelete!}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default B2BParticipantsCard;
